import React, { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Tooltip,
  Chip,
} from '@mui/material';
import { ActionMenu } from '../tickets/TicketActions/ActionMenu';
import { NotificationBadge } from '../tickets/TicketActions/NotificationBadge';
import { StatusChip } from '../tickets/TicketComponents/StatusChip';
import { PriorityChip } from '../tickets/TicketComponents/PriorityChip';

interface TicketTableProps {
  tickets: any[];
  unreadMessagesCounts: { [key: number]: number };
  onView: (ticket: any) => void;
  onOpenMessages: (ticket: any) => void;
  onEdit: (ticket: any) => void;
  onDelete: (ticketId: number, ticketSubject?: string) => void;
  onEditTicketOpen: (ticket: any) => void;
  onMarkAsRead: (ticketId: number) => void;
  authUser: any;
  modules: any[];
  priorities: any[];
  statuses: any[];
}

export const TicketTable: React.FC<TicketTableProps> = ({
  tickets,
  unreadMessagesCounts,
  onView,
  onOpenMessages,
  onEdit,
  onDelete,
  onEditTicketOpen,
  onMarkAsRead,
  authUser,
  modules,
  priorities // BU EKLENDİ
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'created_at', direction: 'desc' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTickets = useMemo(() => {
    let sorted = [...tickets];

    if (sortConfig) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (sortConfig.key.includes('at')) {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sorted;
  }, [tickets, sortConfig]);

  const paginatedTickets = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedTickets.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedTickets, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const truncate = (s: string | null | undefined, n = 30) =>
    !s ? '' : (s.length > n ? s.slice(0, n).trimEnd() + '…' : s);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR');
  };

  const getModuleName = (ticket: any) => {
    return ticket.module_name || modules.find(m => m.id === ticket.module_id)?.name || 'Belirtilmemiş';
  };

  return (
    <Paper>
      <TableContainer sx={{
        overflowX: 'auto',
        '&::-webkit-scrollbar': { height: 8 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.3)', borderRadius: 4 }
      }}>
        <Table sx={{ minWidth: 'max-content' }}> {/* ✅ min-width değişti */}
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.75rem' }}>
                Destek ID
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.75rem' }}>
                Sıra No
              </TableCell>

              <TableCell
                sx={{ color: 'white', fontWeight: 'bold', textAlign: 'left', cursor: 'pointer', fontSize: '0.75rem' }}
                onClick={() => handleSort('company_name')}
              >
                Firma {sortConfig?.key === 'company_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>

              <TableCell
                sx={{ color: 'white', fontWeight: 'bold', textAlign: 'left', cursor: 'pointer', fontSize: '0.75rem' }}
                onClick={() => handleSort('module_name')}
              >
                Program {sortConfig?.key === 'module_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>

              <TableCell
                sx={{ color: 'white', fontWeight: 'bold', textAlign: 'left', cursor: 'pointer', fontSize: '0.75rem' }}
                onClick={() => handleSort('subject')}
              >
                Konu {sortConfig?.key === 'subject' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>

              <TableCell
                sx={{ color: 'white', fontWeight: 'bold', textAlign: 'left', cursor: 'pointer', fontSize: '0.75rem' }}
                onClick={() => handleSort('status_id')}
              >
                Durum {sortConfig?.key === 'status_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>

              {authUser?.role !== 'customer' && (
                <TableCell
                  sx={{ color: 'white', fontWeight: 'bold', textAlign: 'left', cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={() => handleSort('priority_id')}
                >
                  Öncelik {sortConfig?.key === 'priority_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
              )}

              {authUser?.role !== 'customer' && (
                <TableCell
                  sx={{ color: 'white', fontWeight: 'bold', textAlign: 'left', cursor: 'pointer', fontSize: '0.75rem' }}
                  onClick={() => handleSort('assigned_to_name')}
                >
                  Atanan {sortConfig?.key === 'assigned_to_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </TableCell>
              )}

              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
                onClick={() => handleSort('created_at')}
              >
                Oluşturulma Tarihi {sortConfig?.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>

              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  cursor: 'pointer',
                  fontSize: '0.75rem'
                }}
                onClick={() => handleSort('updated_at')}
              >
                Son Güncelleme Tarihi {sortConfig?.key === 'updated_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </TableCell>

              <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.75rem' }}>
                Bildirim
              </TableCell>

              <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center', fontSize: '0.75rem' }}>
                İşlemler
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTickets.map((ticket, index) => (
              <TableRow
                key={ticket.id}
                hover
                onDoubleClick={() => onView(ticket)}
                sx={{
                  '& td': {
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderBottom: '1px solid #f0f0f0'
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    cursor: 'pointer',
                  },
                  backgroundColor: index % 2 === 0 ? '#f5f5f5' : 'white',
                  borderLeft: ticket.status_id === 1 ? '4px solid #4caf50' : 'none'
                }}
              >
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>{ticket.id}</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {page * rowsPerPage + index + 1}
                </TableCell>

                <TableCell sx={{ textAlign: 'left', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ticket.company_name}
                </TableCell>

                <TableCell sx={{ textAlign: 'left', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getModuleName(ticket)}
                </TableCell>

                <TableCell sx={{ textAlign: 'left', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={ticket.subject || ''} arrow>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {truncate(ticket.subject, 28)}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell sx={{ textAlign: 'left' }}>
                  <StatusChip statusId={ticket.status_id} statuses={[]} />
                </TableCell>

                {authUser?.role !== 'customer' && (
                  <TableCell sx={{ textAlign: 'left' }}>
                    {/* BURASI DÜZELTİLDİ: priorities={[]} yerine priorities={priorities} */}
                    <PriorityChip
                      priorityId={ticket.priority_id}
                      priorities={priorities}
                      authUser={authUser}
                    />
                  </TableCell>
                )}

                {authUser?.role !== 'customer' && (
                  <TableCell sx={{ textAlign: 'left', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.assigned_to_name || '-'}
                  </TableCell>
                )}

                <TableCell sx={{ textAlign: 'center' }}>{formatDate(ticket.created_at)}</TableCell>

                <TableCell sx={{ textAlign: 'center' }}>{formatDate(ticket.updated_at)}</TableCell>

                <TableCell
                  sx={{
                    textAlign: 'center',
                    py: 0.5,
                    width: '50px',
                    minWidth: '50px',
                    maxWidth: '50px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <NotificationBadge
                    ticketId={ticket.id}
                    unreadCount={unreadMessagesCounts[ticket.id] || 0}
                    onMarkAsRead={onMarkAsRead}
                    onOpenMessages={(ticketId) => {
                      // ✅ Ticket objesini bul ve gönder
                      const ticket = tickets.find(t => t.id === ticketId);
                      if (ticket) {
                        onOpenMessages(ticket);
                      }
                    }}
                  />
                </TableCell>

                <TableCell sx={{ textAlign: 'center' }}>
                  <ActionMenu
                    ticket={ticket}
                    authUser={authUser}
                    onView={onView}
                    onEditTicketOpen={onEditTicketOpen}
                    onOpenMessages={onOpenMessages}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={sortedTickets.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Sayfa başına satır:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
        }
        sx={{
          '& .MuiTablePagination-toolbar': {
            padding: '8px 16px',
            minHeight: '52px'
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            margin: 0
          }
        }}
      />

      {tickets.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Henüz hiç talep bulunmuyor
          </Typography>
        </Box>
      )}

      {tickets.length > 0 && paginatedTickets.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Filtrelere uygun talep bulunamadı
          </Typography>
        </Box>
      )}
    </Paper>
  );
};